import React, { useState, ReactElement, useCallback, useEffect, useMemo, memo } from "react";
import Modal, { ModalProps } from "../Modals/Modal";
import Screens, { ScreensProps } from "./screens/Screens";
import { getSnowplowId } from "./DirectApplyUtils";
import { getScreenByStep } from "./constants";
import { JobCategory, JobLocation, TrackingObject } from "./DirectApplyTypes";
import styles from "./DirectApply.module.scss";

export interface DirectApplyProps {
    companyId: string;
    companyName: string;
    endpoint: string;
    isOpen: boolean
    atsJobId: string;
    jobId?: number;
    jobName: string;
    jobCategory: JobCategory | null;
    normalizedTitle: string;
    level: string;
    link: string;
    locations: JobLocation[];
    setIsOpen: (value: boolean | ((prevVar: boolean) => boolean)) => void;
    signUpCallBack: (props: any) => Promise<boolean>;
    service: string;
    showMoreJobsButton?: boolean;
}

const getTrackingProps = (props: {
    jobCategory: JobCategory | null,
    step: number,
    location: string;
}): TrackingObject => {
    const { jobCategory, step, location } = props;
    const screen = getScreenByStep(step)
    return {
        sp_click_button_text: "X",
        sp_click_button_type: null,
        sp_click_component: "button",
        sp_click_insert_type: null,
        sp_click_position: "main",
        sp_click_target_page_section: "job_detail",
        sp_click_link_text: null,
        sp_modal_action_type: "close",
        sp_modal_type: "direct_apply",
        sp_impression_modal_type: "direct_apply",
        sp_impression_search_id: null,
        sp_tile_type: "null",
        sp_click_component_tile_type: null,
        sp_tile_entity_id: "null",
        sp_modal_job_category: jobCategory?.code ?? "",
        sp_modal_screen_name: screen.name,
        sp_modal_screen_order: step,
        sp_modal_job_location: location,
        sp_modal_job_alert_opt_in: null,
        sp_modal_newsletter_opt_in: null,
    };
};

const remoteLocation = {
    id: 86,
    value: "Flexible / Remote",
    city: "Flexible / Remote",
    state: "Flexible / Remote",
    postalCode: "",
    streetAddress: "",
    country: "Flexible / Remote",
    latitude: null,
    longitude: null,
    geocoded_locations: null,
    feed_address: null
};

/**
 * Generates a modal that contains a form for job application.
 * It allows users to fill out the form and submit their application.
 *
 * @returns {ReactElement} The header of the direct apply modal.
 */
const DirectApply = memo((props: DirectApplyProps): ReactElement => {
    const {
        companyId,
        companyName,
        endpoint,
        atsJobId,
        jobId,
        jobName,
        jobCategory,
        normalizedTitle,
        level,
        link,
        locations,
        isOpen = false,
        setIsOpen,
        signUpCallBack,
        service,
        showMoreJobsButton = false
    } = props;

    // if there are no locations in the array, push in the remote one.
    if (!locations.length) {
        locations.push(remoteLocation);
    }

    const [step, setStep] = useState<number>(1);
    const [snowplowId, setSnowplowId] = useState("");

    useEffect(() => {
        getSnowplowId(setSnowplowId);
    }, [])

    const hideModal = useCallback(() => {
        setIsOpen(false);
    }, [setIsOpen]);

    const trackingProps: TrackingObject = useMemo(() => {
        return getTrackingProps({ jobCategory, step, location: locations[0]?.value ?? "" });
    }, [jobCategory, locations, step]);

    const onOpen = useCallback(() => {
        setIsOpen(true);
        // Dialogue will auto focus the first focusable element on the modal. In this case it would be the tooltip.
        // De-focus the tool tip.
        (document.activeElement as HTMLButtonElement).blur();
    }, [setIsOpen]);

    /**
     * Closes the modal and fires any extra tracking events.
     */
    const onClose = useCallback(() => {
        setIsOpen(false);
        document.body.focus();
    }, [setIsOpen]);

    const modalProps: ModalProps = {
        closeButtonClassName: styles.closeButton,
        containerClassName: styles.directApply,
        contentClassName: styles.content,
        isOpen,
        isPayWall: false,
        onClose,
        onOpen,
        setIsOpen,
        timer: 0,
        impressionTrackingProps: {},
        closeTrackingProps: {
            ...trackingProps,
            sp_modal_job_alert_opt_in: null,
            sp_modal_newsletter_opt_in: null,
        },
    };

    const screensProps: ScreensProps = {
        atsJobId,
        companyId,
        companyName,
        endpoint,
        hideModal,
        jobId,
        jobCategory,
        jobName,
        level,
        link,
        locations,
        normalizedTitle,
        onClose,
        service,
        setStep,
        signUpCallBack,
        snowplowId,
        step,
        trackingProps,
        showMoreJobsButton
    };

    return (
        <Modal {...modalProps}>
            <Screens {...screensProps} />
        </Modal>
    );
});
DirectApply.displayName = "DirectApply";

export default DirectApply;
